import img from "../assests/images/header-logo.png";
import img2 from "../assests/images/South-Travels-Footer-Logo.png";

import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  ChevronDown,
  Heart,
  LogOut,
  Menu,
  User,
  X,
} from "lucide-react";

export default function Header({
  onLoginClick,
  user,
  setUser,
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileUserRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // SCROLL DETECTION
  // =====================================================

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // =====================================================
  // CLOSE DROPDOWNS WHEN CLICKING OUTSIDE
  // =====================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        mobileUserRef.current &&
        !mobileUserRef.current.contains(event.target)
      ) {
        setMobileUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =====================================================
  // CLOSE MOBILE MENU WHEN ROUTE CHANGES
  // =====================================================

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // =====================================================
  // PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  // =====================================================

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // =====================================================
  // HELPERS
  // =====================================================

  const navigateTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileUserOpen(false);
  };

  const handleHomeClick = () => {
    closeMobileMenu();
    navigateTop();
  };

  // =====================================================
  // LOGOUT
  // =====================================================

  const logout = () => {
    localStorage.removeItem("user");

    if (setUser) {
      setUser(null);
    }

    setDropdownOpen(false);
    setMobileUserOpen(false);
    setMobileMenuOpen(false);

    navigate("/");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // DESKTOP NAVIGATION
  // =====================================================

  const desktopNavClass = ({ isActive }) => `
    relative
    inline-flex
    items-center
    whitespace-nowrap
    px-1
    py-2.5
    text-[12px]
    xl:text-[13px]
    font-medium
    tracking-[-0.01em]
    transition-all
    duration-200

    after:absolute
    after:left-1/2
    after:-translate-x-1/2
    after:bottom-0
    after:h-[2px]
    after:w-0
    after:rounded-full
    after:bg-[#D9A441]
    after:transition-all
    after:duration-200

    hover:after:w-full

    ${isActive
      ? "text-[#0B4442] after:w-full"
      : "text-[#123F42] hover:text-[#0B4442]"
    }
  `;

  // =====================================================
  // MOBILE NAVIGATION
  // =====================================================

  const mobileNavClass = ({ isActive }) => `
    flex
    w-full
    items-center
    justify-between
    rounded-xl
    px-4
    py-3.5
    text-sm
    font-medium
    transition-all
    duration-200

    ${isActive
      ? "bg-[#F6F0E4] text-[#0B4442]"
      : "text-[#163F41] hover:bg-[#FAF7F0] hover:text-[#0B4442]"
    }
  `;

  return (
    <>
      {/* =====================================================
          MAIN HEADER
      ===================================================== */}

      <header
        className={`
    fixed
    top-0
    left-0
    z-[1000]
    w-full
    bg-[#FCFBF7]
    transition-all
    duration-300
    ${isScrolled
            ? "shadow-[0_4px_20px_rgba(12,55,54,0.10)]"
            : "shadow-[0_1px_8px_rgba(12,55,54,0.04)]"
          }
  `}
      >
        <div
          className="
      mx-auto
      w-full
      max-w-[1440px]
      px-5
      sm:px-8
      lg:px-12
      xl:px-16
    "
        >
          <div
            className="
        flex
        min-h-[80px]
        items-center
        justify-between
        gap-6
        sm:min-h-[88px]
        lg:min-h-[96px]
      "
          >

            {/* =================================================
          LOGO
      ================================================= */}

            <NavLink
              to="/"
              onClick={handleHomeClick}
              className="
          relative
          z-10
          flex
          shrink-0
          items-center
          self-stretch
        "
              aria-label="Dubai Hotel Rates home"
            >
              <img
                src={img}
                alt="Dubai Hotel Rates"
                className="
            block
            h-auto
            w-[150px]
            max-w-full
            object-contain
            sm:w-[165px]
            md:w-[180px]
            lg:w-[190px]
            xl:w-[160px]
          "
              />
            </NavLink>

            {/* =================================================
                DESKTOP NAVIGATION
            ================================================= */}

            <nav
              className="
                hidden
                lg:flex
                flex-1
                items-center
                justify-center
                gap-6
                xl:gap-8
                2xl:gap-9
              "
              aria-label="Main navigation"
            >
              <NavLink
                to="/"
                onClick={navigateTop}
                className={desktopNavClass}
              >
                Home
              </NavLink>

              <NavLink
                to="/hotels"
                className={desktopNavClass}
              >
                Hotels
              </NavLink>

              <NavLink
                to="/destinations"
                className={desktopNavClass}
              >
                Destinations
              </NavLink>

              <NavLink
                to="/services"
                className={desktopNavClass}
              >
                Services
              </NavLink>

              <NavLink
                to="/blogs"
                className={desktopNavClass}
              >
                Blog
              </NavLink>


              <NavLink
                to="/about-us"
                className={desktopNavClass}
              >
                About Us
              </NavLink>

            </nav>

            {/* =================================================
                DESKTOP RIGHT SIDE
            ================================================= */}

            <div
              className="
                hidden
                lg:flex
                shrink-0
                items-center
                gap-4
                xl:gap-5
              "
            >
              {/* =================================================
                  SIGN IN / USER
              ================================================= */}

              {!user ? (
                <button
                  type="button"
                  onClick={onLoginClick}
                  className="
                    flex
                    items-center
                    gap-2
                    whitespace-nowrap
                    text-[12px]
                    font-medium
                    text-[#123F42]
                    transition-colors
                    duration-200
                    hover:text-[#0B4442]
                  "
                >
                  <User
                    size={17}
                    strokeWidth={1.8}
                  />

                  <span>
                    Sign In
                  </span>
                </button>
              ) : (
                <div
                  ref={dropdownRef}
                  className="relative"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setDropdownOpen(
                        (prev) => !prev
                      )
                    }
                    className="
                      flex
                      items-center
                      gap-2
                      rounded-full
                      px-1.5
                      py-1
                      transition
                      hover:bg-[#F5F1E8]
                    "
                  >
                    <span
                      className="
                        flex
                        h-8
                        w-8
                        items-center
                        justify-center
                        rounded-full
                        bg-[#0B4442]
                        text-xs
                        font-semibold
                        text-white
                      "
                    >
                      {user?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "U"}
                    </span>

                    <span
                      className="
                        max-w-24
                        truncate
                        text-xs
                        font-semibold
                        text-[#123F42]
                      "
                    >
                      {user?.name?.split(" ")[0] ||
                        "Account"}
                    </span>

                    <ChevronDown
                      size={14}
                      className={`
                        text-[#526B6A]
                        transition-transform
                        duration-200
                        ${dropdownOpen
                          ? "rotate-180"
                          : ""
                        }
                      `}
                    />
                  </button>

                  {/* USER DROPDOWN */}

                  {dropdownOpen && (
                    <div
                      className="
                        absolute
                        right-0
                        top-full
                        mt-3
                        w-52
                        overflow-hidden
                        rounded-xl
                        border
                        border-[#E9E3D8]
                        bg-white
                        py-1
                        shadow-[0_15px_40px_rgba(10,52,51,0.14)]
                      "
                    >
                      <NavLink
                        to="/profile"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#FAF7F0]
                          hover:text-[#0B4442]
                        "
                      >
                        <User size={17} />

                        My Profile
                      </NavLink>

                      <NavLink
                        to="/wishlist"
                        onClick={() =>
                          setDropdownOpen(false)
                        }
                        className="
                          flex
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-[#314F50]
                          transition
                          hover:bg-[#FAF7F0]
                          hover:text-[#0B4442]
                        "
                      >
                        <Heart size={17} />

                        Wishlist
                      </NavLink>



                      <div className="my-1 border-t border-[#EEE9DF]" />

                      <button
                        type="button"
                        onClick={logout}
                        className="
                          flex
                          w-full
                          items-center
                          gap-3
                          px-4
                          py-3
                          text-sm
                          text-red-500
                          transition
                          hover:bg-red-50
                        "
                      >
                        <LogOut size={17} />

                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* =================================================
                  MY BOOKINGS
              ================================================= */}
              <NavLink
                to="/contact-us"
                className="
                  inline-flex
                  items-center
                  justify-center
                  whitespace-nowrap
                  rounded-lg
                  bg-[#E0B35A]
                  px-5
                  py-2.5
                  text-[11px]
                  font-semibold
                  text-[#173D3D]
                  shadow-[0_4px_12px_rgba(181,137,57,0.16)]
                  transition-all
                  duration-200
                  hover:-translate-y-0.5
                  hover:bg-[#D6A84D]
                  hover:shadow-[0_7px_18px_rgba(181,137,57,0.22)]
                "
              >
                Contact Us
              </NavLink>

            </div>

            {/* =================================================
                MOBILE MENU BUTTON
            ================================================= */}

            <button
              type="button"
              onClick={() =>
                setMobileMenuOpen(true)
              }
              aria-label="Open navigation menu"
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-lg
                border
                border-[#DCD7CC]
                bg-[#FCFBF7]
                text-[#0B4442]
                transition-all
                duration-200
                hover:bg-[#F5F1E8]
                lg:hidden
              "
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* =====================================================
          MOBILE OVERLAY
      ===================================================== */}

      <div
        onClick={closeMobileMenu}
        aria-hidden={!mobileMenuOpen}
        className={`
          fixed
          inset-0
          z-[1090]
          bg-[#092E2D]/50
          backdrop-blur-sm
          transition-opacity
          duration-300
          lg:hidden

          ${mobileMenuOpen
            ? "visible opacity-100"
            : "pointer-events-none invisible opacity-0"
          }
        `}
      />

      {/* =====================================================
          MOBILE SIDEBAR
      ===================================================== */}

      <aside
        aria-label="Mobile navigation"
        className={`
          fixed
          left-0
          top-0
          z-[1100]
          h-dvh
          w-[88%]
          max-w-[380px]
          overflow-y-auto
          bg-[#FCFBF7]
          shadow-2xl
          transition-transform
          duration-300
          ease-out
          lg:hidden

          ${mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full"
          }
        `}
      >
        {/* =================================================
            MOBILE HEADER
        ================================================= */}

        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-[#214D4B]
            bg-[#0B4442]
            px-5
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className="flex items-center"
          >
            <img
              src={img2}
              alt="Dubai Hotel Rates"
              className="
                h-auto
                w-[140px]
                object-contain
              "
            />
          </NavLink>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close navigation menu"
            className="
              flex
              h-10
              w-10
              items-center
              justify-center
              rounded-full
              bg-white/10
              text-white
              transition
              hover:bg-white/20
            "
          >
            <X size={22} />
          </button>
        </div>

        {/* =================================================
            MOBILE USER
        ================================================= */}

        <div
          className="
            border-b
            border-[#EAE4D8]
            px-5
            py-4
          "
        >
          {user ? (
            <div
              ref={mobileUserRef}
              className="relative"
            >
              <button
                type="button"
                onClick={() =>
                  setMobileUserOpen(
                    (prev) => !prev
                  )
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-3
                  text-left
                "
              >
                <span
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-[#0B4442]
                    text-base
                    font-bold
                    text-white
                  "
                >
                  {user?.name
                    ?.charAt(0)
                    ?.toUpperCase() || "U"}
                </span>

                <span
                  className="
                    min-w-0
                    flex-1
                  "
                >
                  <span
                    className="
                      block
                      truncate
                      text-sm
                      font-bold
                      text-[#163F41]
                    "
                  >
                    {user?.name || "Account"}
                  </span>

                  <span
                    className="
                      block
                      text-xs
                      text-[#718080]
                    "
                  >
                    Tap to manage account
                  </span>
                </span>

                <ChevronDown
                  size={18}
                  className={`
                    text-[#718080]
                    transition-transform
                    duration-200

                    ${mobileUserOpen
                      ? "rotate-180"
                      : ""
                    }
                  `}
                />
              </button>

              {mobileUserOpen && (
                <div
                  className="
                    mt-3
                    overflow-hidden
                    rounded-xl
                    border
                    border-[#E9E3D8]
                    bg-[#F8F5EE]
                  "
                >
                  <NavLink
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B4442]
                    "
                  >
                    <User size={17} />

                    My Profile
                  </NavLink>

                  <NavLink
                    to="/wishlist"
                    onClick={closeMobileMenu}
                    className="
                      flex
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-[#314F50]
                      hover:bg-white
                      hover:text-[#0B4442]
                    "
                  >
                    <Heart size={17} />

                    Wishlist
                  </NavLink>



                  <button
                    type="button"
                    onClick={logout}
                    className="
                      flex
                      w-full
                      items-center
                      gap-3
                      px-4
                      py-3
                      text-sm
                      text-red-500
                      hover:bg-red-50
                    "
                  >
                    <LogOut size={17} />

                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                onLoginClick?.();
                closeMobileMenu();
              }}
              className="
                flex
                w-full
                items-center
                justify-center
                gap-2
                rounded-lg
                bg-[#E0B35A]
                px-4
                py-3.5
                text-sm
                font-semibold
                text-[#173D3D]
                shadow-md
                transition
                hover:bg-[#D6A84D]
              "
            >
              <User size={17} />

              Sign In / Register
            </button>
          )}
        </div>

        {/* =================================================
            MOBILE NAVIGATION
        ================================================= */}

        <nav
          className="
            space-y-1
            px-4
            py-5
          "
        >
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className={mobileNavClass}
          >
            Home
          </NavLink>

          <NavLink
            to="/hotels"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Hotels
          </NavLink>

          <NavLink
            to="/destinations"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Destinations
          </NavLink>

          <NavLink
            to="/blogs"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Blog
          </NavLink>

          <NavLink
            to="/about-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            About Us
          </NavLink>

          <NavLink
            to="/contact-us"
            onClick={closeMobileMenu}
            className={mobileNavClass}
          >
            Contact
          </NavLink>

          {/* =================================================
              MOBILE MY BOOKINGS
          ================================================= */}



          {/* =================================================
              MOBILE WISHLIST
          ================================================= */}

          <NavLink
            to="/wishlist"
            onClick={closeMobileMenu}
            className="
              mt-3
              flex
              items-center
              justify-center
              gap-2
              rounded-lg
              border
              border-[#E3DDD1]
              bg-white
              px-4
              py-3.5
              text-sm
              font-semibold
              text-[#0B4442]
              transition
              hover:bg-[#F8F5EE]
            "
          >
            <Heart size={18} />

            Wishlist

            <span
              className="
                rounded-full
                bg-[#0B4442]
                px-2
                py-0.5
                text-[11px]
                text-white
              "
            >
              0
            </span>
          </NavLink>
        </nav>
      </aside>
    </>
  );
}